import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './ContactList.css';
import { LuSearch, LuSettings, LuPlug, LuTriangleAlert, LuChartNoAxesColumn, LuFilterX, LuArchive, LuArrowLeft, LuPin, LuBellOff, LuClock } from 'react-icons/lu';
import { FaInstagram } from 'react-icons/fa';
import { FiltrosBotao, FiltrosPainel, FiltrosResumo, FiltroColuna, FiltroCampo, FiltroChips, FiltroMulti, type FiltroOpcao } from './ListKit';
import { nomeEtapa } from '../data/etapas';
import { PlataformaEnum, nomeDoCanal } from '../services/ConexaoPlataformaService';
import { TipoContatoService, type TipoContato } from '../services/TipoContatoService';
import { fraseDaOrigem, nomeDoAnuncio } from '../services/TrafegoPagoService';
import { temFuncionalidade } from '../config/permissions';
import type { Contact } from '../data/mockData';
import { useNavigate } from 'react-router-dom';
import { useCelular } from '../hooks/useCelular';
import MenuContexto from './MenuContexto';
import { itensDaConversa, type AcoesConversa } from './conversaMenu';
import { textoDaVolta } from '../lib/prazosChat';

/** Onde a lista está: a caixa de entrada ou uma das duas pastas irmãs (Arquivadas e Lembretes). */
export type ModoLista = 'caixa' | 'arquivadas' | 'lembretes';

const PASTAS = {
  arquivadas: { titulo: 'Arquivadas', vazia: 'Nenhuma conversa arquivada.' },
  lembretes: { titulo: 'Lembretes', vazia: 'Nenhuma conversa em Lembretes.' },
} as const;

/* A amostra NÃO carrega hex: a cor sai do mesmo token que pinta a conversa
   (`--ch-bg-<id>` em ChatScreen.css), senão ela mostra o valor do tema claro
   enquanto a tela está no escuro — era o que acontecia. */
export const FUNDOS = [
  { id: 'bege', nome: 'Bege' },
  { id: 'areia', nome: 'Areia' },
  { id: 'azul', nome: 'Azul da marca' },
  { id: 'cinza', nome: 'Cinza' },
  { id: 'branco', nome: 'Branco' },
] as const;

/** Quanto o dedo precisa ficar parado na linha para abrir o menu (o toque curto continua abrindo a conversa). */
const TOQUE_LONGO_MS = 500;

const HORA = 3600000;
const DIA = 24 * HORA;

/**
 * Os dois filtros de tempo olham o MESMO campo (a última mensagem) por lados opostos, e é de
 * propósito que sejam dois: "Hoje" responde *o que mexeu*, "Parada há mais de 3 dias" responde
 * *o que estou deixando morrer*. Juntos num campo só, um dos dois sempre ficaria escondido.
 */
const PERIODOS_H: Record<string, number> = { 'Hoje': DIA, '7 dias': 7 * DIA, '30 dias': 30 * DIA };
const PARADAS_H: Record<string, number> = { '24h': DIA, '3 dias': 3 * DIA, '7 dias': 7 * DIA };

/** OrigemMensagemEnum da API: 0 Cliente, 1 Vendedor, 2 IA. */
const ORIGEM_CLIENTE = 0;

interface ContactListProps {
  /** A caixa de entrada: as conversas que NÃO estão arquivadas nem esperando um lembrete. */
  contacts: Contact[];
  /** O conteúdo da pasta Arquivadas. Mora no ChatPage, que recarrega a lista em várias situações. */
  arquivadas: Contact[];
  /** O conteúdo da pasta Lembretes (lembrete ainda por vencer). */
  lembretes: Contact[];
  modo: ModoLista;
  onAbrirPasta: (pasta: 'arquivadas' | 'lembretes') => void;
  onFecharPasta: () => void;
  acoes: AcoesConversa;
  activeContactId: number;
  onSelectContact: (id: number) => void;
  whatsappStatus: 'checking' | 'connected' | 'disconnected' | 'no-instance' | null;
  /** Plano de fundo da conversa, escolhido na engrenagem. */
  fundo: string;
  onFundoChange: (fundo: string) => void;
  textura: boolean;
  onTexturaChange: (ligada: boolean) => void;
  /** Quem está olhando: é o "Eu" do filtro de responsável, e quem ele traz marcado ao abrir. */
  usuarioId?: number | null;
  /** Tem `conversas.todos`: a lista veio com a empresa inteira, então o filtro de pessoas faz sentido.
   *  Sem a permissão a API só mandou as dele — não haveria o que escolher, e o campo não aparece. */
  podeVerDeOutros?: boolean;
}

const so = (valor: string) => valor.replace(/\D/g, '');

const getInitials = (name: string) => {
  const limpo = name.trim();
  if (!limpo) return 'C';
  if (/^\+?\d[\d\s()-]*$/.test(limpo)) return limpo.replace(/\D/g, '').slice(-2) || 'C';
  const partes = limpo.split(/\s+/);
  if (partes.length === 1) return partes[0].charAt(0).toUpperCase();
  return (partes[0].charAt(0) + partes[partes.length - 1].charAt(0)).toUpperCase();
};

const ContactList: React.FC<ContactListProps> = ({
  contacts,
  arquivadas,
  lembretes,
  modo,
  onAbrirPasta,
  onFecharPasta,
  acoes,
  activeContactId,
  onSelectContact,
  whatsappStatus,
  fundo,
  onFundoChange,
  textura,
  onTexturaChange,
  usuarioId,
  podeVerDeOutros,
}) => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<'all' | 'unread'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [popAberto, setPopAberto] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);
  const gearRef = useRef<HTMLButtonElement>(null);
  /* Os filtros moram no painel do kit de listagem, o mesmo das Oportunidades: a faixa da lista tem
     271px e não cabe pílula, e o conjunto passou de dois itens. "Todas" e "Não lidas" continuam
     fora dele, como pílulas — são os dois recortes que se usam o tempo todo. */
  const [filtroAberto, setFiltroAberto] = useState(false);
  const filtroBtnRef = useRef<HTMLButtonElement>(null);
  /* Responsável guarda a CHAVE, não o nome: `u:<id>` para pessoa e `ia` para o que está com o robô.
     Ver FiltroOpcao no ListKit. */
  const [responsavel, setResponsavel] = useState<string[]>([]);
  const [periodo, setPeriodo] = useState<string | null>(null);
  const [parada, setParada] = useState<string | null>(null);
  const [aguardando, setAguardando] = useState(false);
  const [foraDoFunilFiltro, setForaDoFunilFiltro] = useState(false);
  const [etapa, setEtapa] = useState<string[]>([]);
  /* A aba de canal é EXCLUSIVA (uma de cada vez), diferente dos outros filtros: é a leitura da
     referência que ele mandou — "Tudo / WhatsApp / Instagram" é um lugar onde se está, não uma
     combinação que se monta. */
  const [canalAba, setCanalAba] = useState<string>('todos');
  const [temperatura, setTemperatura] = useState<string[]>([]);
  /* Tipo de contato: casa por ID, porque o nome é editável pela empresa. A lista vem uma vez. */
  const [tipoContato, setTipoContato] = useState<string[]>([]);
  const [tiposContato, setTiposContato] = useState<TipoContato[]>([]);
  const usaTipoContato = temFuncionalidade('tipo_contato');

  useEffect(() => {
    if (!usaTipoContato) return;
    let vivo = true;
    TipoContatoService.getAll()
      .then(r => { if (vivo && r.sucesso) setTiposContato(r.dados || []); })
      .catch(() => { /* sem a lista o filtro não aparece; o Chat abre igual */ });
    return () => { vivo = false; };
  }, [usaTipoContato]);
  const listaRef = useRef<HTMLDivElement>(null);
  const celular = useCelular();

  /* Menu de contexto da linha. `toque` guarda o toque longo em andamento; `disparou` impede que o
     clique que o navegador solta ao levantar o dedo abra a conversa por cima do menu. */
  const [menu, setMenu] = useState<{ contato: Contact; x: number; y: number; porToque?: boolean } | null>(null);
  const toque = useRef<{ timer: number | null; disparou: boolean; quando: number }>(
    { timer: null, disparou: false, quando: 0 },
  );
  const fecharMenu = useCallback(() => setMenu(null), []);

  const cancelarToque = useCallback(() => {
    if (toque.current.timer !== null) {
      window.clearTimeout(toque.current.timer);
      toque.current.timer = null;
    }
  }, []);
  useEffect(() => cancelarToque, [cancelarToque]);

  const abrirMenuNaLinha = (e: React.MouseEvent<HTMLButtonElement>, contato: Contact) => {
    /* O menu nativo sai sempre: no Android o toque longo o dispara, e ele atropelaria o nosso. */
    e.preventDefault();
    /* No celular quem abre é o temporizador do toque longo; o `contextmenu` que vem junto é o eco dele. */
    if (celular && Date.now() - toque.current.quando < 1000) return;
    /* Tecla de menu de contexto (ou Shift+F10): vem sem posição, então o menu nasce na linha. */
    if (e.clientX === 0 && e.clientY === 0) {
      const caixa = e.currentTarget.getBoundingClientRect();
      setMenu({ contato, x: caixa.left + 24, y: caixa.bottom - 8 });
      return;
    }
    setMenu({ contato, x: e.clientX, y: e.clientY });
  };

  const comecarToque = (e: React.TouchEvent<HTMLButtonElement>, contato: Contact) => {
    toque.current.quando = Date.now();
    toque.current.disparou = false;
    if (!celular) return;
    const ponto = e.touches[0];
    cancelarToque();
    toque.current.timer = window.setTimeout(() => {
      toque.current.timer = null;
      toque.current.disparou = true;
      setMenu({ contato, x: ponto.clientX, y: ponto.clientY, porToque: true });
    }, TOQUE_LONGO_MS);
  };

  const caiu = whatsappStatus === 'disconnected' || whatsappStatus === 'no-instance';

  useEffect(() => {
    if (!popAberto) return;
    const fora = (e: MouseEvent) => {
      const alvo = e.target as Node;
      if (popRef.current?.contains(alvo) || gearRef.current?.contains(alvo)) return;
      setPopAberto(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setPopAberto(false);
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('keydown', esc);
    };
  }, [popAberto]);

  /* Abre com "Eu" marcado: quem tem a permissão recebe a empresa inteira, e sem isso a tela
     nasceria mostrando as conversas de todo mundo — que não é o que ela mostrava antes. Roda uma
     vez, quando o id chega; depois disso a escolha é da pessoa, inclusive desmarcar. */
  const semeado = useRef(false);
  useEffect(() => {
    if (semeado.current || !podeVerDeOutros || !usuarioId) return;
    semeado.current = true;
    setResponsavel([`u:${usuarioId}`]);
  }, [podeVerDeOutros, usuarioId]);

  /* Conta CONVERSAS, não mensagens — a mesma unidade do sino —, e a marca manual entra: a pill mostra
     o que está pendente, o sino avisa o que chegou. */
  const naoLida = (c: Contact) => c.unread > 0 || c.marcadaNaoLida === true;
  const totalNaoLidas = contacts.filter(naoLida).length;
  const arquivadasComNovidade = arquivadas.some(naoLida);
  const totalForaDoFunil = contacts.filter(c => c.foraDoFunil).length;

  /* Chave do responsável: pessoa por id, e um balde só para o que ainda não tem dono — conversa que
     o robô atende nasce sem `responsibleId`. */
  const chaveResp = (c: Contact) => (c.responsibleId ? `u:${c.responsibleId}` : 'ia');
  const nomeResp = (c: Contact) => (c.responsibleId ? (c.responsibleName || 'Sem nome') : 'Brik AI');

  /** Conta por chave e devolve as opções do filtro, em ordem alfabética. */
  const opcoesDe = (chave: (c: Contact) => string | null, rotulo: (c: Contact) => string): FiltroOpcao[] => {
    const mapa = new Map<string, FiltroOpcao>();
    contacts.forEach(c => {
      const k = chave(c);
      if (k === null) return;
      const atual = mapa.get(k);
      if (atual) atual.total += 1;
      else mapa.set(k, { valor: k, total: 1, rotulo: rotulo(c) });
    });
    return [...mapa.values()].sort((a, b) => (a.rotulo ?? a.valor).localeCompare(b.rotulo ?? b.valor, 'pt-BR'));
  };

  /* "Eu" vem sempre em cima, e com o nome próprio: numa lista de 12 pessoas, procurar o próprio
     nome no meio da ordem alfabética é trabalho à toa. */
  const opcoesResponsavel = useMemo(() => {
    const todas = opcoesDe(chaveResp, nomeResp);
    const minhaChave = usuarioId ? `u:${usuarioId}` : null;
    const minha = minhaChave ? todas.find(o => o.valor === minhaChave) : undefined;
    const resto = todas.filter(o => o.valor !== minhaChave);
    return minha ? [{ ...minha, rotulo: `Eu (${minha.rotulo})` }, ...resto] : todas;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, usuarioId]);

  const opcoesEtapa = useMemo(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    () => opcoesDe(c => (c.statusId ? String(c.statusId) : null), c => nomeEtapa(c.statusId as number)), [contacts]);
  /* Canal de CONVERSA, agrupado como a pessoa pensa: as duas conexões de WhatsApp (oficial e
     não oficial) são "WhatsApp". Manual, RD, Lead Ads e webhook não são canal de atendimento —
     a conversa existe, mas ninguém a procura por "veio do webhook"; elas ficam só em "Tudo".
     Sem isso o filtro mostrava DUAS opções chamadas "WhatsApp" (a 1 e a 6, que nomeDoCanal
     devolve igual) e a pessoa escolhia uma sem entender por que a outra sumia. */
  const grupoCanal = (plataforma?: number | null): string | null =>
    plataforma === PlataformaEnum.WhatsappMeta || plataforma === PlataformaEnum.WhatsappEvolution ? 'whatsapp'
      : plataforma === PlataformaEnum.Instagram ? 'instagram'
      : plataforma === PlataformaEnum.FacebookMessenger ? 'messenger'
      : null;
  const ROTULO_CANAL: Record<string, string> = { whatsapp: 'WhatsApp', instagram: 'Instagram', messenger: 'Messenger' };

  /* As abas só existem quando há mais de um canal na lista: empresa de um canal só não ganha
     uma faixa que não recorta nada. Três das cinco empresas com uso real têm um canal só. */
  const canaisPresentes = useMemo(() => {
    const vistos = new Set<string>();
    for (const c of contacts) {
      const g = grupoCanal(c.plataforma);
      if (g) vistos.add(g);
    }
    return ['whatsapp', 'instagram', 'messenger'].filter(g => vistos.has(g));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts]);
  /* Só os tipos que aparecem na lista; "Sem tipo" no fim e só quando há conversa sem tipo. */
  const opcoesTipoContato = useMemo(() => {
    const comTipo = tiposContato
      .map(t => ({ valor: String(t.tipoContatoId), rotulo: t.nome, total: contacts.filter(c => c.tipoContatoId === t.tipoContatoId).length }))
      .filter(o => o.total > 0);
    const sem = contacts.filter(c => !c.tipoContatoId).length;
    return sem && comTipo.length ? [...comTipo, { valor: '', rotulo: 'Sem tipo', total: sem }] : comTipo;
  }, [contacts, tiposContato]);
  const opcoesTemperatura = useMemo(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    () => opcoesDe(c => c.tipoLeadNome || null, c => c.tipoLeadNome as string), [contacts]);

  const busca = searchTerm.trim().toLowerCase();
  const buscaDigitos = so(searchTerm);
  /* Dentro da pasta não há "todas" nem "não lidas": a lista é só o que está guardado nela. */
  const modoPasta = modo !== 'caixa';
  const base = modo === 'arquivadas' ? arquivadas : modo === 'lembretes' ? lembretes : contacts;

  const agora = Date.now();
  const desde = periodo ? agora - (PERIODOS_H[periodo] ?? 0) : null;
  const ate = parada ? agora - (PARADAS_H[parada] ?? 0) : null;

  const ligados = responsavel.length + etapa.length + temperatura.length + tipoContato.length
    + (periodo ? 1 : 0) + (parada ? 1 : 0) + (aguardando ? 1 : 0) + (foraDoFunilFiltro ? 1 : 0);

  const filtrados = base.filter(c => {
    /* A conversa ABERTA nunca some da lista, qualquer que seja o filtro. Sem isso, marcar um recorte
       com a conversa na tela ao lado deixaria a lista vazia apontando para ela — e, no caso de "Não
       lidas", a linha que a pessoa acabou de abrir sumiria debaixo do cursor, porque abrir já zera. */
    if (c.id === activeContactId) return true;
    /* Dentro da pasta o recorte não vale: Arquivadas e Lembretes mostram o que está guardado nelas. */
    if (!modoPasta) {
      if (activeFilter === 'unread' && !naoLida(c)) return false;
      if (responsavel.length && !responsavel.includes(chaveResp(c))) return false;
      if (etapa.length && !etapa.includes(String(c.statusId ?? ''))) return false;
      if (canalAba !== 'todos' && grupoCanal(c.plataforma) !== canalAba) return false;
      if (temperatura.length && !temperatura.includes(c.tipoLeadNome ?? '')) return false;
      if (tipoContato.length && !tipoContato.includes(String(c.tipoContatoId ?? ''))) return false;
      if (foraDoFunilFiltro && c.foraDoFunil !== true) return false;
      /* Quem falou por último foi o cliente: é o que "não lidas" não pega, porque abrir a conversa
         zera o contador mesmo sem ninguém ter respondido. */
      if (aguardando && c.ultimaMensagemDe !== ORIGEM_CLIENTE) return false;
      const quando = c.ultimaMensagemEm ?? 0;
      if (desde !== null && quando < desde) return false;
      if (ate !== null && quando > ate) return false;
    }
    if (!busca) return true;
    const porNome = c.name.toLowerCase().includes(busca);
    const porTelefone = buscaDigitos.length >= 3 && so(c.phone || '').includes(buscaDigitos);
    return porNome || porTelefone;
  });

  const limparFiltros = () => {
    setResponsavel([]); setEtapa([]); setCanalAba('todos'); setTemperatura([]); setTipoContato([]);
    setPeriodo(null); setParada(null); setAguardando(false); setForaDoFunilFiltro(false);
  };

  /* O resumo conta o recorte em palavra, não em número: o botão já mostra quantos. */
  const resumo: string[] = [];
  if (responsavel.length) {
    const prim = opcoesResponsavel.find(o => o.valor === responsavel[0]);
    resumo.push(responsavel.length === 1 ? (prim?.rotulo ?? 'Responsável') : `${responsavel.length} responsáveis`);
  }
  if (aguardando) resumo.push('Aguardando resposta');
  if (periodo) resumo.push(periodo);
  if (parada) resumo.push(`Parada há +${parada}`);
  if (etapa.length) resumo.push(etapa.length === 1 ? nomeEtapa(Number(etapa[0])) : `${etapa.length} etapas`);
  if (temperatura.length) resumo.push(temperatura.length === 1 ? temperatura[0] : `${temperatura.length} temperaturas`);
  if (tipoContato.length) {
    const prim = opcoesTipoContato.find(o => o.valor === tipoContato[0]);
    resumo.push(tipoContato.length === 1 ? (prim?.rotulo ?? 'Tipo') : `${tipoContato.length} tipos de contato`);
  }
  if (foraDoFunilFiltro) resumo.push('Fora do funil');

  /* `lk-screen` entra pelos TOKENS, não pelo layout: as peças de filtro do kit leem `--lk-*`,
     que só existem nessa casca — fora dela o botão nasceria sem tinta. Sozinha ela declara
     variáveis e nada mais; o layout do kit mora em `.page-shell.lk-screen`, que aqui não há. */
  return (
    <div className="ch-list lk-screen" ref={listaRef}>
      <div className="ch-list-top">
        {/* Canal vem ANTES da busca (ele, 13/10): é a primeira pergunta — "onde eu estou
            olhando" — e só depois se busca ou se recorta dentro daquele canal. Some com um
            canal só, para a lista não perder altura à toa. */}
        {!modoPasta && canaisPresentes.length > 1 && (
          <div className="ch-pills ch-pills-canal">
            <button type="button" className={`ch-pill ${canalAba === 'todos' ? 'is-active' : ''}`}
              onClick={() => setCanalAba('todos')}>Tudo</button>
            {canaisPresentes.map(g => (
              <button key={g} type="button" className={`ch-pill ${canalAba === g ? 'is-active' : ''}`}
                onClick={() => setCanalAba(g)}>{ROTULO_CANAL[g]}</button>
            ))}
          </div>
        )}

        <div className="ch-busca-row">
          <div className="ch-busca">
            <LuSearch size={14} />
            <input
              id="ch-busca-conversas"
              type="text"
              placeholder="Buscar por nome ou telefone"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            ref={gearRef}
            type="button"
            className={`ch-gear ${popAberto ? 'is-open' : ''}`}
            onClick={() => setPopAberto(a => !a)}
            title="Aparência e conexão"
            aria-expanded={popAberto}
          >
            <LuSettings size={16} />
            {caiu && <span className="ch-gear-dot" />}
          </button>
        </div>

        {modoPasta ? (
          <button type="button" className="ch-volta" onClick={onFecharPasta}>
            <LuArrowLeft size={14} />
            <b>{PASTAS[modo as 'arquivadas' | 'lembretes'].titulo}</b>
            <span>{base.length} {base.length === 1 ? 'conversa' : 'conversas'}</span>
          </button>
        ) : (
          <div className="ch-pills">
            <button
              type="button"
              className={`ch-pill ${activeFilter === 'all' ? 'is-active' : ''}`}
              onClick={() => setActiveFilter('all')}
            >
              Todas <b>{contacts.length}</b>
            </button>
            <button
              type="button"
              className={`ch-pill ${activeFilter === 'unread' ? 'is-active' : ''}`}
              onClick={() => setActiveFilter('unread')}
            >
              Não lidas <b>{totalNaoLidas}</b>
            </button>
            {/* O botão abre o painel do kit, ancorado nele. O número de filtros aplicados fica no
                próprio botão, e a linha de resumo embaixo diz QUAIS são — recorte escondido que
                não se anuncia é lista vazia sem explicação. */}
            <FiltrosBotao ligados={ligados} aberto={filtroAberto} ref={filtroBtnRef} onClick={() => setFiltroAberto(a => !a)} />
          </div>
        )}

        {!modoPasta && resumo.length > 0 && (
          <div className="ch-fres-row">
            <FiltrosResumo partes={resumo} />
            <button type="button" className="ch-fres-lp" onClick={limparFiltros}>Limpar</button>
          </div>
        )}

        {!modoPasta && (
          <FiltrosPainel
            open={filtroAberto}
            onClose={() => setFiltroAberto(false)}
            subtitulo={`Aplicados na hora · ${filtrados.length} de ${contacts.length} ${contacts.length === 1 ? 'conversa' : 'conversas'}`}
            ligados={ligados}
            onLimpar={limparFiltros}
            celular={celular}
            verRotulo={`Ver ${filtrados.length} ${filtrados.length === 1 ? 'conversa' : 'conversas'}`}
            ancora={filtroBtnRef}
          >
            <FiltroColuna>
              {/* Quem está com a conversa. Só para quem recebeu a empresa inteira: sem a
                  permissão a lista já é só dele, e o campo teria uma opção. */}
              {podeVerDeOutros && (
                <FiltroCampo rotulo="Responsável" onLimpar={responsavel.length ? () => setResponsavel([]) : undefined}>
                  <FiltroMulti opcoes={opcoesResponsavel} valores={responsavel} onChange={setResponsavel} vazio="Todos" />
                </FiltroCampo>
              )}
              {/* "Fora do funil" só entra quando a empresa marcou alguém assim — na maioria
                  delas o campo seria um chip que nunca recorta nada. */}
              <FiltroCampo rotulo="Situação">
                <FiltroChips
                  opcoes={[{ valor: 'Aguardando resposta' }, ...(totalForaDoFunil > 0 ? [{ valor: 'Fora do funil' }] : [])]}
                  valores={[...(aguardando ? ['Aguardando resposta'] : []), ...(foraDoFunilFiltro ? ['Fora do funil'] : [])]}
                  onChange={v => { setAguardando(v.includes('Aguardando resposta')); setForaDoFunilFiltro(v.includes('Fora do funil')); }}
                />
              </FiltroCampo>
              {opcoesEtapa.length > 1 && (
                <FiltroCampo rotulo="Etapa" onLimpar={etapa.length ? () => setEtapa([]) : undefined}>
                  <FiltroMulti opcoes={opcoesEtapa} valores={etapa} onChange={setEtapa} vazio="Todas" />
                </FiltroCampo>
              )}
            </FiltroColuna>
            <FiltroColuna>
              <FiltroCampo rotulo="Última interação" onLimpar={periodo ? () => setPeriodo(null) : undefined}>
                <FiltroChips
                  unico
                  opcoes={Object.keys(PERIODOS_H).map(v => ({ valor: v }))}
                  valores={periodo ? [periodo] : []}
                  onChange={v => setPeriodo(v[0] ?? null)}
                />
              </FiltroCampo>
              <FiltroCampo rotulo="Parada há mais de" onLimpar={parada ? () => setParada(null) : undefined}>
                <FiltroChips
                  unico
                  opcoes={Object.keys(PARADAS_H).map(v => ({ valor: v }))}
                  valores={parada ? [parada] : []}
                  onChange={v => setParada(v[0] ?? null)}
                />
              </FiltroCampo>
              {/* O Canal NÃO mora aqui: virou a faixa de abas em cima da lista, porque é o
                  recorte que se troca o tempo todo. Temperatura só aparece quando há o que escolher. */}
              {usaTipoContato && opcoesTipoContato.length > 0 && (
                <FiltroCampo rotulo="Tipo de contato" onLimpar={tipoContato.length ? () => setTipoContato([]) : undefined}>
                  <FiltroMulti opcoes={opcoesTipoContato} valores={tipoContato} onChange={setTipoContato} vazio="Todos" />
                </FiltroCampo>
              )}
              {opcoesTemperatura.length > 1 && (
                <FiltroCampo rotulo="Temperatura" onLimpar={temperatura.length ? () => setTemperatura([]) : undefined}>
                  <FiltroChips opcoes={opcoesTemperatura.map(o => ({ valor: o.valor }))} valores={temperatura} onChange={setTemperatura} />
                </FiltroCampo>
              )}
            </FiltroColuna>
          </FiltrosPainel>
        )}

        {popAberto && (
          <div className="ch-pop" ref={popRef}>
            <div className="ch-pop-h">Aparência e conexão</div>

            <div className="ch-pop-s">
              <div className="ch-pop-t">Plano de fundo</div>
              <div className="ch-fundos">
                {FUNDOS.map(f => (
                  <button
                    key={f.id}
                    type="button"
                    className={`ch-fundo ${fundo === f.id ? 'is-active' : ''}`}
                    style={{ background: `var(--ch-bg-${f.id})` }}
                    title={f.nome}
                    aria-label={f.nome}
                    onClick={() => onFundoChange(f.id)}
                  />
                ))}
              </div>
              {/* interruptor com estado: o rótulo não muda e clicar não fecha o
                  menu, senão o efeito acontece fora da vista de quem ligou. */}
              <div className="ch-lin">
                Textura discreta
                <button
                  type="button"
                  className={`ch-switch ${textura ? 'is-on' : ''}`}
                  onClick={() => onTexturaChange(!textura)}
                  role="switch"
                  aria-checked={textura}
                  aria-label="Textura discreta"
                >
                  <i />
                </button>
              </div>
            </div>

            <div className="ch-pop-s">
              <div className="ch-pop-t">Conexão do WhatsApp</div>
              {whatsappStatus === 'checking' || whatsappStatus === null ? (
                <div className="ch-conex">Verificando…</div>
              ) : caiu ? (
                <>
                  <div className="ch-conex">
                    <span className="ch-ponto bad" />
                    <b>Desconectado</b>
                  </div>
                  <div className="ch-conex-sub">Nada entra nem sai desta tela até reconectar.</div>
                  <button type="button" className="ch-acao urg" onClick={() => navigate('/connections')}>
                    <LuTriangleAlert size={13} />
                    Reconectar
                  </button>
                </>
              ) : (
                <>
                  <div className="ch-conex">
                    <span className="ch-ponto ok" />
                    Conectado
                  </div>
                  <div className="ch-conex-sub">API oficial da Meta</div>
                  <button type="button" className="ch-acao" onClick={() => navigate('/connections')}>
                    <LuPlug size={13} />
                    Gerenciar conexões
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="ch-rows">
        {/* A pasta é a primeira linha, acima até das fixadas, e só existe quando há alguma arquivada:
            linha que leva a uma lista vazia é porta para lugar nenhum. */}
        {!modoPasta && arquivadas.length > 0 && (
          <button type="button" className="ch-pasta" onClick={() => onAbrirPasta('arquivadas')}>
            <LuArchive size={15} />
            <span className="ch-pasta-nome">Arquivadas</span>
            {arquivadasComNovidade
              ? <span className="ch-badge">{arquivadas.length}</span>
              : <span className="ch-pasta-n">{arquivadas.length}</span>}
          </button>
        )}
        {/* A segunda pasta, irmã da primeira: mesma altura, logo abaixo dela, e só existe quando há pelo
            menos uma conversa esperando. O relógio ocupa o lugar da caixa. */}
        {!modoPasta && lembretes.length > 0 && (
          <button type="button" className="ch-pasta" onClick={() => onAbrirPasta('lembretes')}>
            <LuClock size={15} />
            <span className="ch-pasta-nome">Lembretes</span>
            <span className="ch-pasta-n">{lembretes.length}</span>
          </button>
        )}
        {filtrados.length === 0 ? (
          <p className="ch-lista-vazia">
            {base.length === 0
              ? (modoPasta ? PASTAS[modo as 'arquivadas' | 'lembretes'].vazia : 'Você ainda não tem conversas.')
              : 'Nenhuma conversa encontrada com esse filtro.'}
          </p>
        ) : (
          filtrados.map(contact => (
            <button
              key={contact.id}
              type="button"
              className={`ch-row ${contact.id === activeContactId ? 'is-active' : ''} ${menu?.contato.id === contact.id ? 'is-ctx' : ''}`}
              onClick={() => {
                if (toque.current.disparou) {
                  toque.current.disparou = false;
                  return;
                }
                onSelectContact(contact.id);
              }}
              onContextMenu={e => abrirMenuNaLinha(e, contact)}
              onTouchStart={e => comecarToque(e, contact)}
              onTouchMove={cancelarToque}
              onTouchEnd={cancelarToque}
              onTouchCancel={cancelarToque}
            >
              <span className="ch-av">{getInitials(contact.name)}</span>
              <span className="ch-row-txt">
                <span className="ch-row-nomelinha">
                  <span className="ch-row-nome">
                    {contact.plataforma === PlataformaEnum.Instagram && (
                      <FaInstagram size={11} className="ch-row-canal" aria-label="Instagram" title="Conversa do Instagram" />
                    )}
                    {contact.name}
                  </span>
                  {contact.silenciadaAte != null && contact.silenciadaAte > Date.now() && (
                    <span className="ch-row-mudo" title="Avisos silenciados" aria-label="Avisos silenciados">
                      <LuBellOff size={12} />
                    </span>
                  )}
                </span>
                {/* Dentro de Lembretes a prévia é QUANDO volta, não o que foi dito: quem abre a pasta quer saber isso. */}
                <span className="ch-row-prev">
                  {modo === 'lembretes' && contact.lembrarEm
                    ? textoDaVolta(contact.lembrarEm)
                    : (contact.lastMessage || '—')}
                </span>
              </span>
              <span className="ch-row-dir">
                {/* A marca de que a conversa veio de anúncio fica AQUI, e não no
                    lugar da prévia: a última mensagem é o que o vendedor usa para
                    triar a fila, e trocá-la pelo nome do anúncio custaria mais do
                    que entrega. O nome inteiro e a campanha ficam um clique
                    adiante, na faixa dentro da conversa. */}
                {/* A marca vai na MESMA linha da hora, não acima: `ch-row-dir` é
                    uma coluna, e empilhar empurrava a data 8px para baixo só nas
                    conversas de anúncio — a coluna da direita dançava de linha
                    para linha (medido). */}
                <span className="ch-row-topo">
                  {contact.fixada && (
                    <span className="ch-row-fixa" title="Fixada no topo" aria-label="Fixada no topo">
                      <LuPin size={12} />
                    </span>
                  )}
                  {contact.foraDoFunil && (
                    <span className="ch-row-fora" title="Fora do funil" aria-label="Fora do funil">
                      <LuFilterX size={12} />
                    </span>
                  )}
                  {/* A origem do lead aparece mesmo com a funcionalidade Anúncios desligada. */}
                  {contact.atribuicaoAnuncio && (
                    <span
                      className="ch-row-anuncio"
                      title={`${fraseDaOrigem(contact.atribuicaoAnuncio)}: ${nomeDoAnuncio(contact.atribuicaoAnuncio)}`}
                      aria-label={`${fraseDaOrigem(contact.atribuicaoAnuncio)} ${nomeDoAnuncio(contact.atribuicaoAnuncio)}`}
                    >
                      <LuChartNoAxesColumn size={12} />
                    </span>
                  )}
                  <span className="ch-hora">{contact.time}</span>
                </span>
                {/* Número ganha da marca manual: com mensagem nova o selo conta; sem ela, o ponto diz
                    só "tem coisa aqui" — não há mensagem para contar. */}
                {contact.unread > 0
                  ? <span className="ch-badge">{contact.unread}</span>
                  : contact.marcadaNaoLida && (
                    <span className="ch-ponto-nl" title="Marcada como não lida" aria-label="Marcada como não lida" />
                  )}
              </span>
            </button>
          ))
        )}
      </div>

      {menu && (
        <MenuContexto
          itens={itensDaConversa(menu.contato, acoes)}
          posicao={{ x: menu.x, y: menu.y }}
          titulo={menu.contato.name}
          herdarDe={listaRef.current}
          aoFechar={fecharMenu}
          abertoPorToque={menu.porToque}
        />
      )}
    </div>
  );
};

export default ContactList;
